/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick, logURlLinkClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';

type SignedOutEmptyCustomizationProps = {
    serviceDocumentationLink: string;
    service:
        | 'customClassification'
        | 'customExtraction'
        | 'conversation'
        | 'orchestration'
        | 'qnaMaker'
        | 'customTranslation'
        | 'customHealthcare'
        | 'customSummarization'
        | 'customSentiment';
};

const messages = defineMessages({
    signUp: { id: 'languageStudio.signedOutEmptyCustomization.signUp', defaultMessage: 'Sign up' },
    signInToAzure: { id: 'languageStudio.signedOutEmptyCustomization.signInToAzure', defaultMessage: 'Sign in with Azure' },
    documentation: { id: 'languageStudio.signedOutEmptyCustomization.documentation', defaultMessage: 'View documentation' },
    needAzureAccount: { id: 'languageStudio.signedOutEmptyCustomization.needAzureAccount', defaultMessage: 'Need Azure account?' },
    qnaMakerTitle: {
        id: 'languageStudio.signedOutEmptyCustomization.qnaMakerTitle',
        defaultMessage: 'Welcome to Custom question answering'
    },
    orchestrationTitle: {
        id: 'languageStudio.signedOutEmptyCustomization.orchestrationTitle',
        defaultMessage: 'Welcome to Orchestration workflow'
    },
    conversationTitle: {
        id: 'languageStudio.signedOutEmptyCustomization.conversationTitle',
        defaultMessage: 'Welcome to Conversational language understanding '
    },
    customSummarizationTitle: {
        id: 'languageStudio.signedOutEmptyCustomization.customSummarizationTitle',
        defaultMessage: 'Welcome to Custom summarization'
    },
    customExtractionTitle: {
        id: 'languageStudio.signedOutEmptyCustomization.customExtractionTitle',
        defaultMessage: 'Welcome to Custom named entity recognition'
    },
    customHealthcareTitle: {
        id: 'languageStudio.signedOutEmptyCustomization.customHealthcareTitle',
        defaultMessage: 'Welcome to Custom Text Analytics for health'
    },
    customSentimentTitle: {
        id: 'languageStudio.signedOutEmptyCustomization.customSentimentTitle',
        defaultMessage: 'Welcome to Custom Sentiment Analysis'
    },
    needAzureAccountCont: {
        id: 'languageStudio.signedOutEmptyCustomization.needAzureAccountCont',
        defaultMessage: 'now to get $200 credit'
    },
    customClassificationTitle: {
        id: 'languageStudio.signedOutEmptyCustomization.customClassificationTitle',
        defaultMessage: 'Welcome to Custom text classification'
    },
    customTranslationTitle: {
        id: 'languageStudio.signedOutEmptyCustomization.customTranslationTitle',
        defaultMessage: 'Welcome to Document Translation'
    },
    customSummarizationDescription: {
        id: 'languageStudio.signedOutEmptyCustomization.customSummarizationDescription',
        defaultMessage:
            'With Custom Summarization, you can build custom AI models to summarize your domain-specific content. By creating a Custom Summarization project, you can iteratively label data, train, evaluate, and improve model performance before deploying your model and making it available for consumption. Sign in with your Azure account here to easily build your Custom Summarization model.'
    },
    orchestrationDescription: {
        id: 'languageStudio.signedOutEmptyCustomization.orchestrationDescription',
        defaultMessage: `With Orchestration workflow, you can build orchestration models to connect Conversational Language Understanding
    (CLU), Question Answering projects and LUIS applications. By creating an Orchestration workflow project, you can
    iteratively label utterances, train, evaluate, and improve model performance before deploying your model and making
    it available for consumption. Sign in with your Azure account here to easily build your Orchestration workflow
    model.`
    },
    qnaMakerDescription: {
        id: 'languageStudio.signedOutEmptyCustomization.qnaMakerDescription',
        defaultMessage: `With Custom question answering, you can build custom knowledge bases to create a natural conversational
        layer over your data that is used to find the most appropriate answer for any question input. By creating a
        custom QA project, you can iteratively add question/answer word, pdf and URL sources, add new question and
        answers on the fly, test and improve your KBs performance before deploying your KB and making it available
        for consumption. Sign in with your Azure account here to easily build your Custom question answering
        knowledge base.`
    },
    conversationDescription: {
        id: 'languageStudio.signedOutEmptyCustomization.conversationDescription',
        defaultMessage: `With Conversational language understanding, you can build custom AI models to custom natural language understanding
    models to predict the overall intention of an incoming utterance and extract important information from it. By creating
    a CLU project, you can iteratively label utterances, train, evaluate, and improve model performance before deploying
    your model and making it available for consumption. Sign in with your Azure account here to easily build your CLU model.`
    },
    customExtractionDescription: {
        id: 'languageStudio.signedOutEmptyCustomization.customExtractionDescription',
        defaultMessage: `With Custom named entity recognition, you can build custom AI models to extract to extract domain-specific
    entities from unstructured text, such as contracts or financial documents. By creating a Custom NER project,
    you can iteratively label data, train, evaluate, and improve model performance before deploying your model
    and making it available for consumption. Sign in with your Azure account here to easily build your Custom
    NER model.`
    },
    customHealthcareDescription: {
        id: 'languageStudio.signedOutEmptyCustomization.customHealthcareDescription',
        defaultMessage: `With Custom Text Analytics for health, you can build custom AI models to extract medical
    entities from unstructured text, such as  medical reports or patient feedback. By creating a Custom Text Analytics for health project,
    you can iteratively label data, train, evaluate, and improve model performance before deploying your model
    and making it available for consumption. Sign in with your Azure account here to easily build your Custom
    Text Analytics for health model.`
    },
    customSentimentDescription: {
        id: 'languageStudio.signedOutEmptyCustomization.customSentimentDescription',
        defaultMessage: `With Custom sentiment, you can build custom AI models to help you find out what people think of your a specific  topic by mining each text for clues about positive, negative or a neutral sentiment. By creating a Custom sentiment project, you can iteratively label and modify your data, train, evaluate, and improve model performance before deploying your model and making it available for consumption. Sign in with your Azure account here to easily build your Custom sentiment model.`
    },
    customClassificationDescription: {
        id: 'languageStudio.signedOutEmptyCustomization.customClassificationDescription',
        defaultMessage: `With Custom text classification, you can build custom AI models to classify text into pre-defined custom classes. By
        creating a custom text classification project, you can iteratively label data, train, evaluate, and improve model
        performance before deploying your model and making it available for consumption. Sign in with your Azure account
        here to easily build your custom classification model.`
    },
    customTranslationDescription: {
        id: 'languageStudio.signedOutEmptyCustomization.customTranslationDescription',
        defaultMessage: `With Document Translation, you can consume the document translation API and start a translation job to batch translate multiple documents across all supported languages and dialects, while preserving original document structure and data format.`
    }
});

const theme = getScopedTheme('LanguageStudio');
const descriptionStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { maxWidth: 535, textAlign: 'center' } });

export const SignedOutEmptyCustomization = (props: SignedOutEmptyCustomizationProps) => {
    const { service, serviceDocumentationLink } = props;
    const { localize } = useLocalization();
    const { dispatcher } = useGlobalState();

    const { serviceName } = getPortalFromUrl();
    let serviceConfigs = { title: '', description: '', imageSrc: '' };

    switch (service) {
        case 'conversation':
            serviceConfigs = {
                title: localize(messages.conversationTitle),
                description: localize(messages.conversationDescription),
                imageSrc: getAssetsUrl(`assets/images/languageStudio/customConversationLogo.svg`)
            };
            break;
        case 'customClassification':
            serviceConfigs = {
                title: localize(messages.customClassificationTitle),
                description: localize(messages.customClassificationDescription),
                imageSrc: getAssetsUrl(`assets/images/languageStudio/customClassificationLogo.svg`)
            };
            break;
        case 'customExtraction':
            serviceConfigs = {
                title: localize(messages.customExtractionTitle),
                description: localize(messages.customExtractionDescription),
                imageSrc: getAssetsUrl(`assets/images/languageStudio/customEntityExtractionLogo.svg`)
            };
            break;
        case 'customHealthcare':
            serviceConfigs = {
                title: localize(messages.customHealthcareTitle),
                description: localize(messages.customHealthcareDescription),
                imageSrc: getAssetsUrl(`assets/images/languageStudio/customHealthCare.svg`)
            };
            break;
        case 'customSentiment':
            serviceConfigs = {
                title: localize(messages.customSentimentTitle),
                description: localize(messages.customSentimentDescription),
                imageSrc: getAssetsUrl(`assets/images/languageStudio/customSentiment.svg`)
            };
            break;
        case 'orchestration':
            serviceConfigs = {
                title: localize(messages.orchestrationTitle),
                description: localize(messages.orchestrationDescription),
                imageSrc: getAssetsUrl(`assets/images/languageStudio/customOrchestrationLogo.svg`)
            };
            break;
        case 'qnaMaker':
            serviceConfigs = {
                title: localize(messages.qnaMakerTitle),
                description: localize(messages.qnaMakerDescription),
                imageSrc: getAssetsUrl(`assets/images/languageStudio/customQuestionsAnsweringLogo.svg`)
            };
            break;
        case 'customTranslation':
            serviceConfigs = {
                title: localize(messages.customTranslationTitle),
                description: localize(messages.customTranslationDescription),
                imageSrc: getAssetsUrl(`assets/images/languageStudio/documentTranslationToolLogo.svg`)
            };
            break;
        case 'customSummarization':
            serviceConfigs = {
                title: localize(messages.customSummarizationTitle),
                description: localize(messages.customSummarizationDescription),
                imageSrc: getAssetsUrl(`assets/images/languageStudio/customSummarizationLogo.svg`)
            };
            break;
        default:
    }

    const handleSignInClick = () => {
        logButtonClick(serviceName, 'ProjectList', 'SignIn');
        dispatcher.dispatch('login');
    };

    const handleDocumentationClick = () => {
        logButtonClick(serviceName, 'ProjectList', 'ViewDocs');
    };

    const handleSignUpClick = () => {
        logURlLinkClick(serviceName, 'ProjectList', 'SignUp');
    };

    return (
        <EmptyStateView
            imageSrc={serviceConfigs.imageSrc}
            message={
                <Stack horizontalAlign="center" tokens={{ childrenGap: 20 }}>
                    <LuisText variant="xxLargePlus" color={theme.signedOutTitleColor}>
                        {serviceConfigs.title}
                    </LuisText>
                    <LuisText styles={descriptionStyles}>{serviceConfigs.description}</LuisText>
                    <Stack tokens={{ childrenGap: 20 }}>
                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                            <PrimaryButton onClick={handleSignInClick}>{localize(messages.signInToAzure)}</PrimaryButton>
                            <DefaultButton href={serviceDocumentationLink} onClick={handleDocumentationClick} target="_blank">
                                {localize(messages.documentation)}
                            </DefaultButton>
                        </Stack>

                        <LuisText>
                            {localize(messages.needAzureAccount)}&nbsp;
                            <ExternalLink href="https://aka.ms/azure-freeaccount" onClick={handleSignUpClick} isUnderlined={false}>
                                {localize(messages.signUp)}
                            </ExternalLink>
                            &nbsp;
                            {localize(messages.needAzureAccountCont)}
                        </LuisText>
                    </Stack>
                </Stack>
            }
        />
    );
};
